// src/components/common/ProgramPhoto.tsx
//
// 지원금 상세 페이지 헤더 이미지.
//
// 지원금별 사진이 있으면 그것을, 없으면 분야 공통 사진을 사용합니다.
// 사진 출처: Unsplash (Unsplash License — 상업적 이용 가능)
//   · 지원금별: public/images/programs/<slug>.jpg
//   · 분야 공통: public/images/categories/<category>.jpg
// 원본 ID 는 scripts/fetch-photos.js, 라이선스는 CREDITS.md 참고.
//
import React from 'react';
import { CategoryId } from '../../types';

/** 지원금별 사진의 대체 텍스트 (사진 내용 설명 — 접근성·이미지 검색용) */
const programAlt: Record<string, string> = {
    /* 근로자·일자리 */
    'geunro-jangryeogeum': '봉투에 담긴 지폐',
    'janyeo-jangryeogeum': '아기를 안고 웃는 어머니',
    'silup-geubyeo': '책상에서 이력서를 살펴보는 모습',
    'naeil-baeum-card': '직업훈련 강의를 듣는 수강생들',
    'cheongnyeon-iljari-doyak': '채용 면접에서 악수를 나누는 모습',
    durunuri: '작은 상점에서 일하는 직원',
    'yuyeon-geunmu-jangryeogeum': '집에서 노트북으로 재택근무하는 모습',

    /* 주거 */
    'jugeo-geubyeo': '맑은 하늘 아래 늘어선 아파트 단지',
    'cheongnyeon-wolse-jiwon': '침대와 책상이 놓인 원룸 내부',
    'beotimmok-jeonse-daechul': '집 앞에서 열쇠를 건네받는 손',
    'sinsaenga-teungnye-daechul': '갓 태어난 아기를 안고 있는 부부',
    'didimdol-daechul': '집 모형과 현관 열쇠',
    'haengbok-jutaek': '해 질 무렵의 공공임대 아파트 건물',

    /* 출산·육아 */
    'cheotmannam-iyongkwon': '어른의 손을 잡은 갓난아기의 손',
    'bumo-geubyeo': '아기를 함께 안고 있는 부모',
    'adong-sudang': '손가락으로 가리키며 노는 아기',
    'yuga-hyujik-geubyeo': '아이를 안고 있는 부모의 모습',
    'yugagi-geunro-danchuk': '집에서 근무 시간을 줄여 일하는 모습',
    'imsin-chulsan-jinryobi': '배를 감싼 임산부의 실루엣',
    'ai-dolbom-service': '아이와 함께 블록을 가지고 노는 돌보미',

    /* 교육 */
    'gukga-janghakgeum': '졸업식에서 학사모를 들어 올린 졸업생',
    'gyoyuk-geubyeo': '교실에서 노트북으로 공부하는 학생',
    'pyeongsaeng-gyoyuk-voucher': '성인 학습자를 대상으로 강의하는 모습',
    'cheongnyeon-dojeon-jiwon': '프로그램에 참여해 둘러앉은 청년들',
    'chwieop-hu-sanghwan-daechul': '도서관 서가에서 책을 고르는 학생',

    /* 창업·소상공인 */
    'yebi-changeop-package': '사업계획을 논의하는 창업 준비 모임',
    'chogi-changeop-package': '사무실에서 회의하는 스타트업 팀',
    'sosanggongin-jeongchaek-jageum': '자신의 가게를 지키는 소상공인',
    'noran-usan-gongje': '동전이 놓인 돼지 저금통',
    'huimang-return-package': '지역 상점을 응원하는 문구가 붙은 가게 창문',

    /* 복지 */
    'saenggye-geubyeo': '마트에서 장을 보는 모습',
    'uiryo-geubyeo': '병상의 환자를 진료하는 의사',
    'gicho-yeongeum': '집에서 함께 웃고 있는 노부부',
    'gingeup-bokji-jiwon': '위로하듯 두 손을 맞잡은 모습',
    'energy-voucher': '창가에 설치된 난방기',
    'tongsinbi-gammyeon': '손에 들고 사용하는 스마트폰',
    'chasangwi-gyecheung': '탁자 위에 나란히 모은 여러 사람의 손',
    'noin-iljari': '환하게 웃는 어르신',
};

/** 지원금별 사진이 없을 때 쓰는 분야 공통 사진 */
const categoryFallback: Record<CategoryId, { src: string; alt: string }> = {
    worker: { src: '/images/categories/worker.jpg', alt: '계산기와 노트북으로 소득을 계산하는 모습' },
    housing: { src: '/images/categories/housing.jpg', alt: '맑은 하늘 아래 늘어선 아파트 단지' },
    parenting: { src: '/images/categories/parenting.jpg', alt: '어른의 손을 잡은 갓난아기의 손' },
    education: { src: '/images/categories/education.jpg', alt: '졸업식에서 학사모를 들어 올린 졸업생' },
    business: { src: '/images/categories/business.jpg', alt: '자신의 가게에 서 있는 소상공인' },
    welfare: { src: '/images/categories/welfare.jpg', alt: '어르신의 손을 맞잡아 주는 모습' },
};

interface ProgramPhotoProps {
    category: CategoryId;
    /** 지원금 slug — 지원금별 사진을 찾는 데 사용 */
    slug?: string;
    className?: string;
}

const ProgramPhoto: React.FC<ProgramPhotoProps> = ({ category, slug, className }) => {
    const hasOwn = Boolean(slug && programAlt[slug]);
    const src = hasOwn ? `/images/programs/${slug}.jpg` : categoryFallback[category].src;
    const alt = hasOwn ? programAlt[slug as string] : categoryFallback[category].alt;

    return (
        <img
            className={className}
            src={src}
            alt={alt}
            width={640}
            height={427}
            loading="lazy"
            decoding="async"
        />
    );
};

export default ProgramPhoto;
